import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, Directive, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSourceBackendExtended } from "@comunicare/comunicare-angular-interfaces";
import { GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { Tools } from "@comunicare/comunicare-helper";
import { DataType, IFilter, IItem, IPreference, ITableParameter, PreferenceContext } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subject, merge } from "rxjs";
import { first, skipWhile, takeUntil, tap } from "rxjs/operators";
import { Slide } from "src/app/helpers/helpData";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";

@Directive()
export abstract class AbstractBackendTablePage<T> implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  public filters: IFilter[] = [];
  public displayedColumns: string[] = [];
  public availableColumns: string[] = [];
  public availableColumnItems: IItem[] = [];
  public actionOnMultiple = false;
  public isFiltered = false;
  public globalSearchValue = "";
  public totalCount = 0;
  public currentPageSize = 25;
  public loading = false;
  public isAllServices = true;

  protected dataSource: MatTableDataSourceBackendExtended<T>;
  protected readonly DATA_TYPE = DataType;
  protected onDestroy$ = new Subject<void>();

  constructor(
    protected preferenceService: PreferenceService,
    protected responsiveService: ResponsiveService,
    protected userService: UserService,
    protected sessionService: SessionService,
    protected healthcareService: HealthcareserviceService,
    protected responsiveDialog: ResponsiveDialogService,
    protected translateService: TranslateService
  ) {}

  // ---- Abstract methods to implements ----
  protected abstract getPreferenceContext(): PreferenceContext;
  protected abstract initDataSource(): void;
  protected abstract getServices(): string[];
  protected abstract getHelpSlides(): Slide[];

  async ngOnInit(): Promise<void> {
    this.initDataSource();
    if (
      this.sessionService.currentService &&
      this.userService.ownOrganization &&
      (this.healthcareService.availableServices()?.length > 0 || this.healthcareService.availableMonitoringServices().length > 0)
    ) {
      this.setupServicesWatch();
    } else {
      this.sessionService.servicesSetupWatch
        .pipe(
          skipWhile(() => !this.userService.ownOrganization),
          first()
        )
        .subscribe(() => {
          this.setupServicesWatch();
        });
    }
    await this.loadPreferences();
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.sort.sortChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        if (this.paginator) this.paginator.pageIndex = 0;
      });

      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadData()),
          takeUntil(this.onDestroy$)
        )
        .subscribe();
    }

    if (this.paginator) {
      this.currentPageSize = this.paginator.pageSize;
      this.paginator.page.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.currentPageSize = this.paginator.pageSize;
        this.updatePreference();
      });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public openHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.getHelpSlides() },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  // ---- Service management ----
  private setupServicesWatch(): void {
    if (this.userService.isMonitoringUser) {
      this.setupServices();
      this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupServices();
      });
    } else {
      this.setupServices();
      this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupServices();
      });
    }
  }

  private async setupServices(): Promise<void> {
    if (!this.sessionService.currentService || (this.userService.isMonitoringUser && !this.sessionService.currentMonitoringService)) {
      this.isAllServices = true;
      await this.loadData();
      return;
    }
    if (this.userService.isMonitoringUser) {
      this.isAllServices = this.sessionService.isAllServices(true) || this.sessionService.isAllServices(false);
    } else {
      this.isAllServices = this.sessionService.isAllServices(false);
    }
    await this.loadData();
  }

  // ---- Data management ----
  public async loadData(): Promise<void> {
    this.loading = true;
    const services = this.getServices();
    const data = await this.dataSource.loadData({
      filters: this.getFiltersFormatted(),
      search: this.globalSearchValue,
      sortId: this.sort?.active,
      sortOrder: this.sort?.direction,
      pageNumber: this.paginator?.pageIndex ?? 0,
      pageSize: this.paginator?.pageSize ?? 25,
      services: services,
      lang: this.translateService.currentLang,
      organization: this.sessionService.organization?.reference,
    });
    this.totalCount = data.length;
    this.loading = false;
  }

  public async applyFilter(filter: IFilter): Promise<void> {
    this.dataSource.setFilter(filter);
    this.filters = Tools.deepCopy(this.dataSource.getAllFilters());
    this.isFiltered = this.filters.length > 0;
    await this.loadData();
    this.updatePreference();
  }

  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public async clearFilter(): Promise<void> {
    this.dataSource.clearFilter();
    this.filters = [];
    this.isFiltered = false;
    this.globalSearchValue = "";
    if (this.paginator) this.paginator.pageIndex = 0;
    await this.loadData();
    this.updatePreference();
  }

  public async updateSearch(value?: string): Promise<void> {
    this.globalSearchValue = value || "";
    await this.loadData();
  }

  protected getFiltersFormatted(): IFilter[] {
    const filters = Tools.deepCopy(this.dataSource.getAllFilters());
    const formatted: IFilter[] = [];

    filters.forEach((f) => {
      switch (f.dataType) {
        case DataType.DATE:
          f.data.fromDate = moment(f.data.fromDate).format("YYYY-MM-DD");
          f.data.toDate = moment(f.data.toDate).format("YYYY-MM-DD");
          break;
        case DataType.CHOICE:
          f.data = { value: (f.data || []).filter((c) => c.checked).map((c) => c.value) };
          break;
      }
      formatted.push(f);
    });

    return formatted;
  }

  // ---- Preferences ----
  protected updatePreference(): void {
    if (this.responsiveService.isMobile) return;
    const pref: IPreference = {
      context: this.getPreferenceContext(),
      parameters: {
        filters: this.dataSource.getAllFilters(),
        availableColumnItems: this.availableColumnItems,
        itemsPerPage: this.paginator?.pageSize ?? 25,
      } as ITableParameter,
    };
    this.preferenceService.update(pref).pipe(takeUntil(this.onDestroy$)).subscribe();
  }

  protected async loadPreferences(): Promise<void> {
    const params = await this.preferenceService.list(this.getPreferenceContext()).pipe(first()).toPromise();
    if (params) {
      if (params.filters?.length) {
        for (const f of params.filters) {
          await this.applyFilter(f);
        }
      }
      this.currentPageSize = params.itemsPerPage ?? 25;
      this.availableColumnItems = (params.availableColumnItems as IItem[]).filter((c) => this.availableColumns.includes(c.value));
    }
    this.getDisplayedColumns();
  }

  // ---- Columns ----
  public availableColumnChanged(): void {
    this.displayedColumns = this.availableColumnItems.filter((c) => c.checked).map((c) => c.value);
    this.updatePreference();
  }

  public getDisplayedColumns(): void {
    const cols = this.availableColumnItems.filter((c) => c.checked).map((c) => c.value);
    this.displayedColumns = this.actionOnMultiple ? ["select", ...cols] : cols;
  }

  public columnDropped(event: CdkDragDrop<string[]>): void {
    const realIndexFrom =
      this.availableColumnItems.findIndex((i) => this.displayedColumns[event.previousIndex] === i.value) + (this.actionOnMultiple ? 1 : 0);
    const realIndexTo =
      this.availableColumnItems.findIndex((i) => this.displayedColumns[event.currentIndex] === i.value) + (this.actionOnMultiple ? 1 : 0);
    moveItemInArray(this.availableColumnItems, realIndexFrom, realIndexTo);
    this.updatePreference();
  }

  public activateMultipleCheckBox(): void {
    this.actionOnMultiple = !this.actionOnMultiple;
  }
}
